import { useRef, useEffect, useState } from "react";
import { gsap } from "gsap";
import { ScrollSmoother } from "gsap/ScrollSmoother";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { ScrollToPlugin } from "gsap/ScrollToPlugin";
import { usePathname } from "next/navigation";
import { useGSAP } from "@gsap/react";

let pluginsRegistered = false;
if (typeof window !== 'undefined' && !pluginsRegistered) {
	gsap.registerPlugin(ScrollTrigger, ScrollSmoother, ScrollToPlugin);
	pluginsRegistered = true;
}

export const useSmoothScroll = (isMobile, isLoading, onLoadComplete) => {
	const el = useRef();
	const pathname = usePathname();
	const smootherRef = useRef(null);

	useGSAP(() => {
		if (typeof window === 'undefined') {
			if (onLoadComplete) onLoadComplete();
			return;
		}

		// Clean up any existing ScrollSmoother and ScrollTriggers
		if (smootherRef.current) {
			smootherRef.current.kill();
			smootherRef.current = null;
		}
		ScrollTrigger.getAll().forEach(st => st.kill());

		// IMPORTANT: Only create ScrollSmoother on desktop
		if (isMobile) {
			// Ensure native scroll behavior on mobile
			document.body.style.overflow = '';
			document.documentElement.style.overflow = '';

			// Remove any scroll-blocking styles
			const wrapper = document.getElementById('smooth-wrapper');
			const content = document.getElementById('smooth-content');

			if (wrapper) {
				wrapper.style.overflow = '';
				wrapper.style.position = '';
				wrapper.style.height = '';
			}

			if (content) {
				content.style.overflow = '';
				content.style.position = '';
				content.style.height = '';
			}

			// Refresh ScrollTrigger for mobile (without ScrollSmoother)
			ScrollTrigger.refresh(true);

			if (onLoadComplete) onLoadComplete();
			return;
		}

		// Desktop: Create ScrollSmoother
		try {
			smootherRef.current = ScrollSmoother.create({
				wrapper: "#smooth-wrapper",
				content: "#smooth-content",
				smooth: 1,
				effects: false,
				normalizeScroll: false,
				ignoreMobileResize: true,
				smoothTouch: true, // This is crucial - prevents touch scroll issues
				preventDefault: true,
				ease: "power1.out",
				renderByPixels: true,
				fastScrollEnd: true,
				smoothRender: true,
				debounce: false,
				lockAxis: true,
				lazyRender: true,
				overscrollBehavior: "none",
			});

			ScrollTrigger.refresh(true);
			window.smoothScroll = smootherRef.current;

			if (onLoadComplete) onLoadComplete();
		} catch (error) {
			console.error('Error creating ScrollSmoother:', error);
			// Fallback: ensure native scroll works
			document.body.style.overflow = '';
			document.documentElement.style.overflow = '';
			if (onLoadComplete) onLoadComplete();
		}
	}, [isMobile, pathname, isLoading, onLoadComplete]);

	// Cleanup effect
	useEffect(() => {
		return () => {
			if (smootherRef.current) {
				smootherRef.current.kill();
				smootherRef.current = null;
			}
			ScrollTrigger.getAll().forEach(st => st.kill());

			// Restore native scroll on cleanup
			if (typeof window !== 'undefined') {
				document.body.style.overflow = '';
				document.documentElement.style.overflow = '';
			}
		};
	}, []);

	return el;
};